import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useReviewStore } from '@stores/reviewStore'
import { useAuthStore } from '@stores/authStore'
import { useThemeStore } from '@stores/themeStore'
import { formatDistanceToNow } from 'date-fns'
import {
    ChatBubbleLeftRightIcon,
    ClockIcon,
    PencilSquareIcon,
    XMarkIcon,
    CheckCircleIcon,
    TrashIcon,
    PlusIcon,
    ArrowPathIcon,
    EyeIcon
} from '@heroicons/react/24/outline'
import clsx from 'clsx'
import InputDialog from '@components/common/InputDialog'
import ConfirmDialog from '@components/common/ConfirmDialog'
import { VersionDiffView } from '@components/latex/VersionDiffView'

interface ReviewSidebarProps {
    isOpen: boolean
    onClose: () => void
    documentId?: string | null
    currentFileId?: string | null  // For LaTeX projects: current file ID to show version history
    currentFileName?: string | null  // For LaTeX projects: current file name
    onFileRestored?: () => Promise<void>  // Callback to refresh editor content and compile after restore
}

const ReviewSidebar: React.FC<ReviewSidebarProps> = ({ isOpen, onClose, documentId, currentFileId, currentFileName, onFileRestored }) => {
    const {
        activeTab,
        setActiveTab,
        comments,
        history,
        trackChanges,
        createThread,
        resolveThread,
        deleteComment,
        replyToThread,
        activeThreadId,
        setActiveThreadId
    } = useReviewStore() as any

    const { user } = useAuthStore()
    const { effectiveTheme } = useThemeStore()
    const isDark = effectiveTheme === 'dark'
    const [showCommentDialog, setShowCommentDialog] = useState(false)
    const [pendingComment, setPendingComment] = useState<{ anchor: any, text: string } | null>(null)

    if (!isOpen) return null

    const handleCreateComment = (anchor: any, text: string) => {
        setPendingComment({ anchor, text })
        setShowCommentDialog(true)
    }

    const handleCommentConfirm = async (body: string) => {
        if (pendingComment && body.trim()) {
            await createThread(pendingComment.anchor, body.trim())
            setShowCommentDialog(false)
            setPendingComment(null)
        }
    }

    return (
        <div className={clsx(
            "h-full flex flex-col",
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
        )}>
            {/* Header */}
            <div className={clsx(
                "flex items-center justify-between p-4 border-b",
                isDark ? "border-gray-700" : "border-gray-200"
            )}>
                <h2 className={clsx(
                    "font-semibold text-lg",
                    isDark ? "text-white" : "text-gray-900"
                )}>Review</h2>
                <button
                    onClick={onClose}
                    className={clsx(
                        "rounded-lg p-1.5 transition-colors",
                        isDark 
                            ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700" 
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                    )}
                    title="Close Review Panel"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>

            {/* Tabs */}
            <div className={clsx(
                "flex border-b",
                isDark ? "border-gray-700" : "border-gray-200"
            )}>
                <TabButton
                    active={activeTab === 'comments'}
                    onClick={() => setActiveTab('comments')}
                    icon={<ChatBubbleLeftRightIcon className="w-4 h-4" />}
                    label="Comments"
                    isDark={isDark}
                />
                <TabButton
                    active={activeTab === 'history'}
                    onClick={() => setActiveTab('history')}
                    icon={<ClockIcon className="w-4 h-4" />}
                    label="History"
                    isDark={isDark}
                />
                <TabButton
                    active={activeTab === 'track-changes'}
                    onClick={() => setActiveTab('track-changes')}
                    icon={<PencilSquareIcon className="w-4 h-4" />}
                    label="Changes"
                    isDark={isDark}
                />
            </div>

            {/* Content */}
            <div className={clsx(
                "flex-1 overflow-y-auto p-4",
                isDark ? "bg-gray-900/50" : "bg-gray-50/50"
            )}>
                <AnimatePresence mode="wait">
                    {activeTab === 'comments' && (
                        <CommentsList
                            comments={comments}
                            onResolve={resolveThread}
                            onDelete={deleteComment}
                            onReply={replyToThread}
                            currentUserId={user?.id}
                            isDark={isDark}
                        />
                    )}
                    {activeTab === 'history' && (
                        <HistoryList 
                            history={history} 
                            isDark={isDark}
                            documentId={documentId}
                            currentFileId={currentFileId}
                            currentFileName={currentFileName}
                            onFileRestored={onFileRestored}
                        />
                    )}
                    {activeTab === 'track-changes' && (
                        <TrackChangesList changes={trackChanges} isDark={isDark} />
                    )}
                </AnimatePresence>
            </div>

            {/* Comment Dialog */}
            <InputDialog
                isOpen={showCommentDialog}
                onClose={() => {
                    setShowCommentDialog(false)
                    setPendingComment(null)
                }}
                onConfirm={handleCommentConfirm}
                title="Add Comment"
                message={pendingComment ? `Comment on: "${pendingComment.text.slice(0, 50)}${pendingComment.text.length > 50 ? '...' : ''}"` : "Add a comment"}
                placeholder="Enter your comment..."
                confirmText="Add Comment"
                cancelText="Cancel"
                inputType="textarea"
            />
        </div>
    )
}

const TabButton: React.FC<{ 
    active: boolean
    onClick: () => void
    icon: React.ReactNode
    label: string
    isDark: boolean
}> = ({ active, onClick, icon, label, isDark }) => (
    <button
        onClick={onClick}
        className={clsx(
            "flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors relative",
            active
                ? isDark
                    ? "text-blue-400 bg-blue-900/20"
                    : "text-blue-600 bg-blue-50/30"
                : isDark
                    ? "text-gray-400 hover:text-gray-200 hover:bg-gray-700/50"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
        )}
    >
        {icon}
        <span>{label}</span>
        {active && (
            <motion.div
                layoutId="activeTab"
                className={clsx(
                    "absolute bottom-0 left-0 right-0 h-0.5",
                    isDark ? "bg-blue-400" : "bg-blue-600"
                )}
            />
        )}
    </button>
)

const CommentsList: React.FC<any> = ({ comments, onResolve, onDelete, onReply, currentUserId, isDark }) => {
    if (!comments?.length) {
        return (
            <div className={clsx(
                "flex flex-col items-center justify-center h-full text-sm",
                isDark ? "text-gray-500" : "text-gray-400"
            )}>
                <ChatBubbleLeftRightIcon className={clsx(
                    "w-12 h-12 mb-2",
                    isDark ? "opacity-30" : "opacity-20"
                )} />
                <p>No comments yet</p>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            {comments.map((thread: any) => (
                <div key={thread.id} className={clsx(
                    "p-3 rounded-lg shadow-sm border",
                    isDark 
                        ? "bg-gray-800 border-gray-700" 
                        : "bg-white border-gray-200"
                )}>
                    <div className="flex justify-between items-start mb-2">
                        <div className={clsx(
                            "text-xs font-medium px-2 py-0.5 rounded",
                            isDark
                                ? "text-blue-400 bg-blue-900/20"
                                : "text-blue-600 bg-blue-50"
                        )}>
                            "{thread.anchor?.quote?.slice(0, 50)}..."
                        </div>
                        <button
                            onClick={() => onResolve(thread.id)}
                            className={clsx(
                                "transition-colors",
                                isDark
                                    ? "text-gray-500 hover:text-green-400"
                                    : "text-gray-400 hover:text-green-600"
                            )}
                            title="Resolve thread"
                        >
                            <CheckCircleIcon className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="space-y-3">
                        {thread.comments.map((comment: any) => (
                            <div key={comment.id} className="text-sm">
                                <div className="flex justify-between mb-1">
                                    <span className={clsx(
                                        "font-medium",
                                        isDark ? "text-gray-200" : "text-gray-900"
                                    )}>{comment.user_info?.full_name || 'User'}</span>
                                    <span className={clsx(
                                        "text-xs",
                                        isDark ? "text-gray-500" : "text-gray-400"
                                    )}>{formatDistanceToNow(new Date(comment.created_at))} ago</span>
                                </div>
                                <p className={clsx(
                                    isDark ? "text-gray-300" : "text-gray-700"
                                )}>{comment.body}</p>
                            </div>
                        ))}
                    </div>

                    <div className={clsx(
                        "mt-3 pt-2 border-t flex gap-2",
                        isDark ? "border-gray-700" : "border-gray-200"
                    )}>
                        <input
                            type="text"
                            placeholder="Reply..."
                            className={clsx(
                                "flex-1 text-sm rounded-md px-2 py-1.5 focus:outline-none focus:ring-2",
                                isDark
                                    ? "bg-gray-700 border-gray-600 text-gray-200 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500"
                                    : "border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500"
                            )}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    onReply(thread.id, (e.target as HTMLInputElement).value)
                                    ; (e.target as HTMLInputElement).value = ''
                                }
                            }}
                        />
                    </div>
                </div>
            ))}
        </div>
    )
}

const HistoryList: React.FC<{
    history: any[]
    isDark: boolean
    documentId?: string | null
    currentFileId?: string | null
    currentFileName?: string | null
    onFileRestored?: () => Promise<void>
}> = ({ history, isDark, documentId, currentFileId, currentFileName, onFileRestored }) => {
    const [fileVersions, setFileVersions] = React.useState<any[]>([])
    const [loadingVersions, setLoadingVersions] = React.useState(false)
    const [selectedVersion, setSelectedVersion] = React.useState<any | null>(null)
    const [showDiff, setShowDiff] = React.useState(false)
    const [showRestoreConfirm, setShowRestoreConfirm] = React.useState<{ version: any | null }>({ version: null })
    const [currentFileContent, setCurrentFileContent] = React.useState<string>('')
    const [restoringVersion, setRestoringVersion] = React.useState<number | null>(null)
    
    // Fetch file versions and current content if we have a LaTeX file open
    useEffect(() => {
        if (documentId && currentFileId) {
            setLoadingVersions(true)
            import('@api/latex').then(({ latexAPI }) => {
                // Fetch versions and current file content in parallel
                Promise.all([
                    latexAPI.getFileVersions(documentId, currentFileId, 50, 0),
                    latexAPI.getFileContent(documentId, currentFileId).then(blob => blob.text())
                ])
                    .then(([versionsResponse, content]) => {
                        setFileVersions(versionsResponse.versions || [])
                        setCurrentFileContent(content)
                        setLoadingVersions(false)
                    })
                    .catch(error => {
                        console.error('Failed to load file versions/content:', error)
                        setLoadingVersions(false)
                    })
            })
        } else {
            setFileVersions([])
            setCurrentFileContent('')
        }
    }, [documentId, currentFileId])
    
    // Restore confirmation handler
    const handleRestore = async () => {
        if (!showRestoreConfirm.version || !documentId || !currentFileId) {
            setShowRestoreConfirm({ version: null })
            return
        }
        
        const version = showRestoreConfirm.version
        setRestoringVersion(version.version_number)
        setShowRestoreConfirm({ version: null })
        
        try {
            const { latexAPI } = await import('@api/latex')
            await latexAPI.restoreFileVersion(
                documentId,
                currentFileId,
                version.version_number,
                true
            )
            // Reload versions and content
            const [versionsResponse, content] = await Promise.all([
                latexAPI.getFileVersions(documentId, currentFileId, 50, 0),
                latexAPI.getFileContent(documentId, currentFileId).then(blob => blob.text())
            ])
            setFileVersions(versionsResponse.versions || [])
            setCurrentFileContent(content)
            setShowDiff(false)
            setSelectedVersion(null)
            
            // Refresh editor content and compile instead of page refresh
            if (onFileRestored) {
                await onFileRestored()
            }
        } catch (error) {
            console.error('Failed to restore version:', error)
            alert('Failed to restore version. Please try again.')
        } finally {
            setRestoringVersion(null)
        }
    }
    
    // Show file versions if available (for LaTeX projects), otherwise show document history
    const hasFileVersions = fileVersions.length > 0
    const hasDocumentHistory = history?.length > 0
    
    if (!hasFileVersions && !hasDocumentHistory) {
        return (
            <div className={clsx(
                "flex flex-col items-center justify-center h-full text-sm",
                isDark ? "text-gray-500" : "text-gray-400"
            )}>
                <ClockIcon className={clsx(
                    "w-12 h-12 mb-2",
                    isDark ? "opacity-30" : "opacity-20"
                )} />
                <p>No history recorded</p>
                {currentFileName && (
                    <p className={clsx(
                        "text-xs mt-2",
                        isDark ? "text-gray-600" : "text-gray-400"
                    )}>
                        File: {currentFileName}
                    </p>
                )}
            </div>
        )
    }
    
    // Show file versions for LaTeX files
    if (hasFileVersions) {
        return (
            <>
                <div className="space-y-3">
                {currentFileName && (
                    <div className={clsx(
                        "text-xs uppercase tracking-wider font-semibold mb-2",
                        isDark ? "text-gray-400" : "text-gray-500"
                    )}>
                        File: {currentFileName}
                    </div>
                )}
                {loadingVersions ? (
                    <div className={clsx(
                        "text-sm text-center py-4",
                        isDark ? "text-gray-400" : "text-gray-500"
                    )}>
                        Loading versions...
                    </div>
                ) : (
                    fileVersions.map((version) => (
                        <div 
                            key={version.id} 
                            className={clsx(
                                "p-3 rounded-lg shadow-sm border transition-colors",
                                isDark
                                    ? "bg-gray-800 border-gray-700 hover:border-blue-600"
                                    : "bg-white border-gray-200 hover:border-blue-300",
                                selectedVersion?.id === version.id && (
                                    isDark ? "ring-2 ring-blue-500" : "ring-2 ring-blue-400"
                                )
                            )}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className={clsx(
                                    "font-medium text-sm",
                                    isDark ? "text-gray-200" : "text-gray-900"
                                )}>
                                    Version {version.version_number}
                                    {version.version_number === fileVersions[0]?.version_number && (
                                        <span className={clsx(
                                            "ml-2 px-2 py-0.5 text-xs rounded",
                                            isDark ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-800"
                                        )}>
                                            Current
                                        </span>
                                    )}
                                </span>
                                <span className={clsx(
                                    "text-xs",
                                    isDark ? "text-gray-500" : "text-gray-400"
                                )}>
                                    {formatDistanceToNow(new Date(version.created_at))} ago
                                </span>
                            </div>
                            {version.change_description && (
                                <div className={clsx(
                                    "text-xs mt-1 italic",
                                    isDark ? "text-gray-400" : "text-gray-500"
                                )}>
                                    {version.change_description}
                                </div>
                            )}
                            <div className={clsx(
                                "text-xs mt-2 flex items-center justify-between",
                                isDark ? "text-gray-500" : "text-gray-400"
                            )}>
                                <span>{(version.file_size / 1024).toFixed(1)} KB</span>
                                <div className="flex items-center gap-2">
                                    {version.version_number !== fileVersions[0]?.version_number && (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setSelectedVersion(version)
                                                    setShowDiff(true)
                                                }}
                                                className={clsx(
                                                    "px-2 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1",
                                                    isDark
                                                        ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                                )}
                                                title="View differences"
                                            >
                                                <EyeIcon className="w-3 h-3" />
                                                View Diff
                                            </button>
                                            <button
                                                onClick={() => setShowRestoreConfirm({ version })}
                                                disabled={restoringVersion === version.version_number}
                                                className={clsx(
                                                    "px-2 py-1 text-xs font-medium rounded transition-colors flex items-center gap-1",
                                                    isDark
                                                        ? "bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 disabled:opacity-50"
                                                        : "bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                                                )}
                                                title="Restore this version"
                                            >
                                                {restoringVersion === version.version_number ? (
                                                    <>
                                                        <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                                        Restoring...
                                                    </>
                                                ) : (
                                                    <>
                                                        <ArrowPathIcon className="w-3 h-3" />
                                                        Restore
                                                    </>
                                                )}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
                </div>
                
                {/* Diff View Modal */}
                {selectedVersion && (
                    <VersionDiffView
                        isOpen={showDiff}
                        onClose={() => {
                            setShowDiff(false)
                            setSelectedVersion(null)
                        }}
                        versionContent={selectedVersion.content}
                        currentContent={currentFileContent}
                        versionNumber={selectedVersion.version_number}
                        fileName={currentFileName || 'file'}
                        isDark={isDark}
                    />
                )}
                
                {/* Restore Confirmation Dialog */}
                <ConfirmDialog
                    isOpen={showRestoreConfirm.version !== null}
                    onCancel={() => setShowRestoreConfirm({ version: null })}
                    onConfirm={handleRestore}
                    title="Restore File Version"
                    message={
                        showRestoreConfirm.version
                            ? `Restore "${currentFileName}" to version ${showRestoreConfirm.version.version_number}? Your current content will be saved as a new version first.`
                            : ''
                    }
                    confirmText="Restore"
                    cancelText="Cancel"
                    variant="info"
                />
            </>
        )
    }

    return (
        <div className="space-y-3">
            {history.map((item: any) => (
                <div key={item.id} className={clsx(
                    "p-3 rounded-lg shadow-sm border transition-colors cursor-pointer",
                    isDark
                        ? "bg-gray-800 border-gray-700 hover:border-blue-600"
                        : "bg-white border-gray-200 hover:border-blue-300"
                )}>
                    <div className="flex justify-between items-start mb-1">
                        <span className={clsx(
                            "font-medium text-sm",
                            isDark ? "text-gray-200" : "text-gray-900"
                        )}>{item.source === 'ai' ? 'AI Edit' : 'User Edit'}</span>
                        <span className={clsx(
                            "text-xs",
                            isDark ? "text-gray-500" : "text-gray-400"
                        )}>{formatDistanceToNow(new Date(item.created_at))} ago</span>
                    </div>
                    <div className={clsx(
                        "text-xs mb-2",
                        isDark ? "text-gray-400" : "text-gray-500"
                    )}>
                        Versions {item.base_version} - {item.final_version}
                    </div>
                    {item.summary && (
                        <p className={clsx(
                            "text-sm",
                            isDark ? "text-gray-300" : "text-gray-600"
                        )}>{item.summary}</p>
                    )}
                </div>
            ))}
        </div>
    )
}

const TrackChangesList: React.FC<any> = ({ changes, isDark }) => {
    if (!changes?.length) {
        return (
            <div className={clsx(
                "flex flex-col items-center justify-center h-full text-sm",
                isDark ? "text-gray-500" : "text-gray-400"
            )}>
                <PencilSquareIcon className={clsx(
                    "w-12 h-12 mb-2",
                    isDark ? "opacity-30" : "opacity-20"
                )} />
                <p>No pending changes</p>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            <div className={clsx(
                "text-xs uppercase tracking-wider font-semibold mb-2",
                isDark ? "text-gray-400" : "text-gray-500"
            )}>Pending Changes</div>
            {changes.map((change: any) => (
                <div key={change.id} className={clsx(
                    "p-3 rounded-lg shadow-sm border",
                    isDark
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-200"
                )}>
                    <div className="flex items-center gap-2 mb-2">
                        <span className={clsx(
                            "w-2 h-2 rounded-full",
                            change.source === 'ai' ? 'bg-purple-500' : 'bg-blue-500'
                        )} />
                        <span className={clsx(
                            "font-medium text-sm",
                            isDark ? "text-gray-200" : "text-gray-900"
                        )}>
                            {change.source === 'ai' ? 'AI Suggestion' : 'User Edit'}
                        </span>
                    </div>
                    <div className={clsx(
                        "text-sm",
                        isDark ? "text-gray-300" : "text-gray-600"
                    )}>
                        {change.title || "Changes pending review"}
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button className={clsx(
                            "flex-1 py-1.5 px-2 text-xs font-medium rounded transition-colors",
                            isDark
                                ? "bg-blue-900/30 text-blue-400 hover:bg-blue-900/50"
                                : "bg-blue-50 text-blue-700 hover:bg-blue-100"
                        )}>
                            Accept
                        </button>
                        <button className={clsx(
                            "flex-1 py-1.5 px-2 text-xs font-medium rounded transition-colors",
                            isDark
                                ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                                : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                        )}>
                            Reject
                        </button>
                    </div>
                </div>
            ))}
        </div>
    )
}

export default ReviewSidebar
